export default function MonthToMonthTenancies() {
  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-calendar-event-line"></i>
            <span>Flexible Tenancies</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Managing Month-to-Month Tenancies During Sale
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Month-to-month tenants offer flexibility but create income uncertainty. Understanding how to manage them during your sale is crucial.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Pros and Cons of Month-to-Month Tenancies</h3>
            
            <p className="text-slate-700 mb-6 leading-relaxed">
              Month-to-month tenancies occur when fixed-term leases expire and tenants continue occupying without signing new lease agreements. While they provide flexibility, they also create challenges during property sales.
            </p>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 mb-6">
              <h4 className="font-semibold text-teal-600 mb-4 flex items-center gap-2">
                <i className="ri-thumb-up-line"></i>
                Advantages for Sellers
              </h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Flexibility for Buyer</p>
                    <p className="text-slate-600 text-xs">Buyers can adjust rents, implement new policies, or request tenant departure with minimal notice</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">No Long-Term Obligations</p>
                    <p className="text-slate-600 text-xs">Buyer isn't locked into unfavorable lease terms or below-market rents</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Easier Renovations</p>
                    <p className="text-slate-600 text-xs">Can relocate tenants temporarily for major improvements without lease complications</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 mb-8">
              <h4 className="font-semibold text-red-600 mb-4 flex items-center gap-2">
                <i className="ri-thumb-down-line"></i>
                Disadvantages for Sellers
              </h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Income Instability</p>
                    <p className="text-slate-600 text-xs">Tenants can leave with 30 days notice, creating sudden vacancy and lost income</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Reduced Property Value</p>
                    <p className="text-slate-600 text-xs">High percentage of month-to-month tenants (20%+) can reduce value by 5-10%</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-red-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm mb-1">Buyer Concerns</p>
                    <p className="text-slate-600 text-xs">Lenders and buyers prefer stable, long-term leases for predictable cash flow</p>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">Strategic Approaches to Month-to-Month Tenants</h3>

            <div className="space-y-4">
              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Convert to Fixed-Term Leases</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Offer 12-month lease renewals at current market rates 3-6 months before listing. This stabilizes income and increases property value.
                    </p>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-600">
                        <strong>Incentive:</strong> Offer $100-$200 rent credit or small upgrade for signing 12-month lease
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Market as Buyer Opportunity</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Position month-to-month tenancies as flexibility for buyer to implement their vision. Some buyers prefer this over being locked into long leases.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center flex-shrink-0 font-bold">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Maintain Current Occupancy</h4>
                    <p className="text-slate-600 text-sm">
                      Keep month-to-month tenants happy and in place through closing. Occupied units are better than vacant ones, even without long-term leases.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/a16efeeae8271e2f6eacc8a26c35650d.jpg"
                  alt="Month-to-month tenancy management and flexible lease agreements"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-calculator-line text-amber-600 text-xl"></i>
                Value Impact Calculator
              </h4>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm font-semibold text-slate-900 mb-3">Scenario: 12-unit building</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">10 units with 12-month leases</span>
                      <span className="text-green-600 font-semibold">Full value</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">2 units month-to-month (17%)</span>
                      <span className="text-amber-600 font-semibold">-2% to -3%</span>
                    </div>
                    <div className="border-t border-slate-200 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="font-semibold text-slate-900">Total Impact</span>
                        <span className="font-bold text-amber-600">-$30K to -$50K</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm font-semibold text-slate-900 mb-3">High Month-to-Month Scenario</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">7 units with 12-month leases</span>
                      <span className="text-green-600 font-semibold">Partial value</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">5 units month-to-month (42%)</span>
                      <span className="text-red-600 font-semibold">-8% to -12%</span>
                    </div>
                    <div className="border-t border-slate-200 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="font-semibold text-slate-900">Total Impact</span>
                        <span className="font-bold text-red-600">-$120K to -$200K</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-4">Buyer Preferences by Type</h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3 pb-3 border-b border-slate-100">
                  <i className="ri-building-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Institutional Buyers</p>
                    <p className="text-slate-600 text-xs">Prefer 80%+ units with 6+ months remaining on leases. Month-to-month reduces appeal significantly.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 pb-3 border-b border-slate-100">
                  <i className="ri-user-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Individual Investors</p>
                    <p className="text-slate-600 text-xs">More flexible. Some prefer month-to-month for ability to renovate and reposition property quickly.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-hammer-line text-teal-600 text-xl mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Value-Add Buyers</p>
                    <p className="text-slate-600 text-xs">Actually prefer month-to-month tenancies for flexibility to implement improvements and raise rents.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Best Practices for Month-to-Month Tenancies During Sale</h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-text-line text-teal-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Document Everything</h4>
              <p className="text-slate-600 text-sm mb-3">
                Maintain clear records of when leases converted to month-to-month, payment history, and tenant quality.
              </p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>• Original lease expiration date</li>
                <li>• Length of month-to-month tenancy</li>
                <li>• Payment reliability record</li>
                <li>• Any verbal agreements</li>
              </ul>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-teal-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Provide Stability Assurance</h4>
              <p className="text-slate-600 text-sm mb-3">
                Reassure month-to-month tenants that their tenancy will continue under new ownership unless they choose to leave.
              </p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>• Written confirmation of continuity</li>
                <li>• Introduction to new owner</li>
                <li>• Clear communication about changes</li>
                <li>• Respect for existing arrangements</li>
              </ul>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-price-tag-3-line text-teal-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Price Appropriately</h4>
              <p className="text-slate-600 text-sm mb-3">
                Adjust asking price to reflect income uncertainty from month-to-month tenancies, or convert to fixed leases before listing.
              </p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>• Reduce price 2-3% per 10% M2M</li>
                <li>• Or convert to fixed terms first</li>
                <li>• Disclose percentage to buyers</li>
                <li>• Highlight tenant quality/longevity</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
